import { isArray, isEmpty } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  useDeleteFarmGateProcessData,
  useGetFacilityWholeList,
  useGetFarmGateProcessDataById,
  useGetTransportTypesList,
  usePostFarmGateProcessData,
  usePutFarmGateProcessData
} from "../../../../../api/cultivations";
import { useGetChemicalTypes } from "../../../../../api/static-data";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { DrawingContext } from "../../../../../components/drawing-tool/DrawingTool";
import { isNodeLabelExists } from "../../../../../components/drawing-tool/utils";
import NoteAndDocument from "../../../../../components/noteAndDocument/NoteAndDocument";
import ProcessingDetails from "../../../../../components/processingDetails/ProcessingDetails";
import { WasteManagementContent } from "../../../../../components/wasteManagment/WasteManagementModal";
import { dataTypeOptions } from "../../../../../config/constants";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import { IngredientDataTypesOption } from "../../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { NodeLabelExistError } from "../../../../../config/enums/NodeLabelError.enum";
import { AccordionTypes, validateAccordionOpen } from "../../../../../helpers/AccordionHelper";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { removeNewDrawingItemStatus, saveNewDrawingItem } from "../../../../../utils/cacheStorage";
import { ChemicalContent } from "../../../../cultivation/processing/ChemicalContent";
import AddIngredientModal from "../../../../cultivation/processing/IngredientContentModal";
import RecipeContent from "../../../../cultivation/processing/RecipeModal";
import SipContentContent from "../../../../cultivation/processing/SipContentModal";
import ManageFacilityData from "../../../../facility/ManageFacilityData";

interface SaveDocumentData {
  saveDocumentAndNote(id: any): void;
  deleteComment(): void;
}

interface ProcessFormData {
  processingName: string;
  processingType: number;
  facilityName: string;
  machineModelNo: string;
  capacity: number;
  capacityUnit?: number;
  input: number;
  inputUnit?: number;
  output: number;
  outputUnit?: number;
  sideStream: number;
  sideStreamUnit?: number;
  heat: number;
  heatUnit?: number;
  electricity: number;
  electricityUnit?: number;
  water: number;
  waterUnit?: number;
  citricAcid?: number;
  dataType?: number;
  heatSource?: any[];
}

export const FarmGateProcessBase = ({ modalData }: any) => {
  const { farmGateId, ingredientId, supplierId, nodeItem } = modalData;
  const { deleteElements, getEdges } = useReactFlow();
  const {
    nodeItemId,
    chartEdges,
    chartNodes,
    setNodeItem,
    setChartNodes,
    lockStatus,
    deleteLockStatus: rawDeleteLockStatus,
    saveDrawing
  } = useContext(DrawingContext);
  const deleteLockStatus = rawDeleteLockStatus ?? lockStatus;

  const [processId, setProcessId] = useState<string>(nodeItem?.data?.reference?.processId);
  const [viewIngredient, setViewIngredient] = useState(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const ref = useRef<SaveDocumentData>(null);
  const [accordionType, setAccordionType] = useState<AccordionTypes | null>(null);
  const [cipSipData, setCipSipData] = useState<any>([]);
  const [visibleFacilityManager, setVisibleFacilityManager] = useState<boolean>(false);

  const {
    data: facilityWholeList,
    isSuccess: isFacilitySuccess,
    refetch: refetchFacilityList
  } = useGetFacilityWholeList(FacilityType.FACTORY);

  const { mutate: postFarmGateProcessData, isLoading: isCreateProcessLoading } =
    usePostFarmGateProcessData();
  const { mutate: putFarmGateProcessData, isLoading: isUpdateProcessLoading } =
    usePutFarmGateProcessData();
  const { mutate: deleteFarmGateProcessData, isLoading: isDeleteProcessLoading } =
    useDeleteFarmGateProcessData();

  const { data, isLoading, refetch } = useGetFarmGateProcessDataById(processId);
  const { data: TransportTypeList } = useGetTransportTypesList();
  const { data: chemicalDataType } = useGetChemicalTypes();

  const sortedChemicalDataType = useMemo(() => {
    return chemicalDataType?.sort((a: any, b: any) => a.text.localeCompare(b.text));
  }, [chemicalDataType]);

  const chemicalData = data?.chemical || [];
  const wasteManagement = data?.wasteManagements || [];

  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const existingProcessingName = useMemo(() => {
    return data?.processingName;
  }, [data?.processingName]);

  const values = useMemo(() => {
    setCipSipData(data?.cipSip);
    return {
      processingName: data?.processingName,
      processingType: data?.processingType?.toString(),
      facilityName: data?.facilityId,
      dataType: data?.dataType || IngredientDataTypesOption.SPECIFIC,
      machineModelNo: data?.machineModelNo,
      capacity: data?.capacity || "",
      input: data?.input || "",
      output: data?.output || "",
      sideStream: data?.sideStream || "",
      heat: data?.heat || "",
      electricity: data?.electricity || "",
      water: data?.water || "",
      citricAcid: data?.citricAcid || 0,
      heatAmount: data?.heatAmount || 0,
      heatSource: data?.heatSource || [],
      capacityUnit: data?.capacityUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      inputUnit: data?.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      outputUnit: data?.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      sideStreamUnit: data?.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      heatUnit: data?.heatUnit ?? MEASUREMENT_TYPES.ENERGY_MJ_KG,
      electricityUnit: data?.electricityUnit ?? MEASUREMENT_TYPES.POWER_kWh_KG,
      waterUnit: data?.waterUnit ?? MEASUREMENT_TYPES.VOLUME_m3_PER_KG
    };
  }, [data]);

  const form = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid },
    control,
    clearErrors,
    setError,
    setFocus
  } = form;

  const dataType = useWatch({ control, name: "dataType" });
  const facilityId = useWatch({ control, name: "facilityName" });

  // Set default dataType to SPECIFIC if empty
  const effectiveDataType = dataType || IngredientDataTypesOption.SPECIFIC;

  const hideContentGenericStatus = useMemo(() => {
    return effectiveDataType === IngredientDataTypesOption.SPECIFIC;
  }, [effectiveDataType]);

  // Set default dataType in form if empty
  useEffect(() => {
    if (!dataType) {
      setValue("dataType", IngredientDataTypesOption.SPECIFIC, { shouldDirty: false });
    }
  }, [dataType, setValue]);

  useEffect(() => {
    if (isFacilitySuccess) {
      const defaultFacilityId = data?.facilityId || facilityWholeList?.[0]?.value;
      setValue("facilityName", defaultFacilityId, { shouldDirty: false });
    }
  }, [isFacilitySuccess, data, facilityWholeList, setValue]);

  const handleAccordion = (requestType: AccordionTypes) => {
    if (isDirty && isEmpty(errors)) {
      handleSubmit(data => {
        onSubmit(data, undefined, () => {
          setAccordionType(accordionType === requestType ? null : requestType);
        });
      })();
      return;
    }

    if (validateAccordionOpen(processId, accordionType, requestType)) {
      setAccordionType(accordionType === requestType ? null : requestType);
    }
  };

  const handleChemical = () => {
    if (processId) return true;

    errorView("Please save the process to add chemical details");
    return false;
  };

  const handleFormSubmit = (data: ProcessFormData) => {
    onSubmit(data, undefined, () => {
      setAccordionType(AccordionTypes.RECIPE);
    });
  };

  const handleChemicalAccordionStatus = useCallback(
    (activeStatus: boolean, requestType: any) => {
      const isChemicalsActive = accordionType === AccordionTypes.CHEMICALS;

      // Only change state if it's different from requested
      if (activeStatus && !isChemicalsActive) {
        setAccordionType(AccordionTypes.CHEMICALS);
      } else if (!activeStatus && isChemicalsActive) {
        setAccordionType(null);
      }

      return isChemicalsActive;
    },
    [accordionType]
  );

  const updateNodeData = (data: any, checkIsValidCipSip: boolean) => {
    try {
      const updatedNode = {
        ...nodeItem,
        data: {
          ...nodeItem?.data,
          label: data?.processingName,
          reference: {
            processId: data?._id
          }
        }
      };
      ref?.current?.saveDocumentAndNote(updatedNode?.data?.reference?.processId);
      const updatedChartNodes = chartNodes.map((n: any) => {
        if (n.id === nodeItem?.id) {
          return updatedNode;
        }
        return n;
      });
      if (checkIsValidCipSip) {
        successMessage("Process updated CIP / SIP successfully");
        return;
      }
      setChartNodes(updatedChartNodes);
      setNodeItem(updatedNode);
      saveDrawing(chartEdges, updatedChartNodes);
      successMessage(processId ? "Process updated successfully" : "Process created successfully");
    } catch (error) {
      errorView(error);
    }
  };

  const onSubmit = (data: any, cipSip?: any, callBack?: any) => {
    const processingNameChanged = getValues("processingName");
    if (
      existingProcessingName !== processingNameChanged &&
      isNodeLabelExists(chartNodes as any, processingNameChanged)
    ) {
      clearErrors("processingName");
      setFocus("processingName");
      setError("processingName", { type: "manual", message: NodeLabelExistError.PROCESSING });
      return;
    }
    let checkIsValidCipSip = isArray(cipSip);
    const dataToCreate = {
      processingName: data?.processingName,
      ingredientId: ingredientId,
      supplierId: supplierId,
      processNumber: 1,
      facilityId: data?.facilityName,
      processingIngredient: [],
      cipSip: checkIsValidCipSip ? cipSip : cipSipData,
      processingType: data?.processingType,
      machineModelNo: data?.machineModelNo,
      capacity: data?.capacity || 0,
      input: data?.input || 0,
      output: data?.output || 0,
      sideStream: data?.sideStream || 0,
      heat: data?.heat || 0,
      electricity: data?.electricity || 0,
      water: data?.water || 0,
      citricAcid: data?.citricAcid || 0,
      processingStatus: "ACTIVE",
      heatSource: data?.heatSource || [],
      farmGateId: farmGateId,
      processId: processId,
      capacityUnit: data?.capacityUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG?.toString(),
      inputUnit: data?.inputUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG?.toString(),
      outputUnit: data?.outputUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG?.toString(),
      sideStreamUnit: data?.sideStreamUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG?.toString(),
      heatUnit: data?.heatUnit?.toString() ?? MEASUREMENT_TYPES.ENERGY_MJ_KG?.toString(),
      electricityUnit:
        data?.electricityUnit?.toString() ?? MEASUREMENT_TYPES.POWER_kWh_KG?.toString(),
      waterUnit: data?.waterUnit?.toString() ?? MEASUREMENT_TYPES.VOLUME_m3_PER_KG?.toString(),
      dataType: effectiveDataType
    };

    if (processId) {
      putFarmGateProcessData(dataToCreate, {
        onSuccess: (data: any) => {
          removeNewDrawingItemStatus();
          refetch();
          updateNodeData(data, checkIsValidCipSip);
          if (callBack && typeof callBack === "function") {
            callBack();
          }
        }
      });
    } else {
      postFarmGateProcessData(dataToCreate, {
        onSuccess: (data: any) => {
          setProcessId(data?._id);
          saveNewDrawingItem("created");
          updateNodeData(data, checkIsValidCipSip);
          if (callBack && typeof callBack === "function") {
            callBack();
          }
        }
      });
    }
  };

  const updateCipSipData = (data: any) => {
    setCipSipData(data);
    const currentData = getValues();
    onSubmit(currentData, data);
  };

  const handleComponentDataSave = (processId: string) => {
    try {
      ref?.current?.saveDocumentAndNote(processId);
    } catch (error) {
      errorView(error);
    }
  };

  const refetchWasteData = () => {
    refetch();
  };

  const onNodesDelete = () => {
    ref?.current?.deleteComment();
    deleteElements({ nodes: [{ id: nodeItem.id }], edges: connectedEdges });
    setNodeItem(null);
  };

  const handleDeleteSuccess = () => {
    onNodesDelete();
    successMessage("Process deleted successfully");
    setVisibleDeleteModal(false);
  };

  const handleDeleteFarmGateProcess = async () => {
    const dataToDelete = {
      farmGateId: farmGateId,
      processId: processId
    };

    if (processId) {
      deleteFarmGateProcessData(dataToDelete, {
        onSuccess: handleDeleteSuccess
      });
      return;
    }
    handleDeleteSuccess();
  };

  if (
    (processId && isLoading) ||
    isCreateProcessLoading ||
    isUpdateProcessLoading ||
    isDeleteProcessLoading
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <div className="tw-w-full tw-p-2">
        <div className="userBankDetailsMain">
          <FormProvider {...form}>
            <ProcessingDetails
              control={control}
              setValue={setValue}
              facilityWholeList={facilityWholeList}
              dataTypeOptions={dataTypeOptions}
              setVisibleFacilityDetailsModal={() => setVisibleFacilityManager(true)}
              refetchFacilityList={refetchFacilityList}
              setViewIngredient={setViewIngredient}
              data={data}
              accordionType={accordionType}
              handleClick={handleAccordion}
              lockStatus={lockStatus}
              deleteLockStatus={deleteLockStatus}
              handleSubmit={handleSubmit}
              handleFormSubmit={handleFormSubmit}
              isValid={isValid}
              setVisibleDeleteModal={setVisibleDeleteModal}
            />
            {hideContentGenericStatus ? (
              <>
                <div className="tw-space-y-0 tw-mt-2">
                  <div className="tw-w-full">
                    <RecipeContent
                      processId={processId}
                      nodeItem={nodeItem}
                      recipeId={data?.recipeId}
                      accordionType={accordionType}
                      refetchProcess={refetch}
                      type="farm-gate"
                      handleAccordion={handleAccordion}
                      onRecipeSave={handleComponentDataSave}
                      lockStatus={lockStatus}
                      disabled={lockStatus}
                    />
                  </div>
                  <div className="tw-w-full tw-pt-0">
                    <SipContentContent
                      getSipData={(data: any) => {
                        updateCipSipData(data);
                      }}
                      accordionType={accordionType}
                      currentCipSipData={data?.cipSip}
                      handleAccordion={handleAccordion}
                      lockStatus={lockStatus}
                    />
                  </div>

                  <div className="tw-w-full tw-pt-0">
                    <ChemicalContent
                      chemicalDataType={sortedChemicalDataType}
                      chemicalData={chemicalData}
                      processId={processId}
                      TransportTypeList={TransportTypeList}
                      refetchChemical={refetch}
                      handleOpenValidation={handleChemical}
                      type="farm"
                      currentAccordionStatus={handleChemicalAccordionStatus}
                      onChemicalSave={handleComponentDataSave}
                      lockStatus={lockStatus}
                    />
                  </div>

                  <div className="tw-w-full tw-pt-0 tw-mb-2">
                    <WasteManagementContent
                      wasteManagement={wasteManagement}
                      processId={processId}
                      accordionType={accordionType}
                      sideStreamValue={values?.sideStream}
                      refetchWasteData={refetchWasteData}
                      type="farm"
                      handleAccordion={handleAccordion}
                      onWasteManagementSave={handleComponentDataSave}
                      lockStatus={lockStatus}
                      parentControl={control}
                    />
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
            <NoteAndDocument
              parentId={processId}
              ref={ref}
              showSaveButton={true}
              isDisabled={lockStatus}
            />
            {visibleFacilityManager && (
              <ManageFacilityData
                facilityId={facilityId}
                closeManager={() => setVisibleFacilityManager(false)}
              />
            )}
          </FormProvider>
        </div>
      </div>

      {/* Modals - Portaled to document.body to escape parent modal stacking context */}
      <AddIngredientModal
        visibleModal={viewIngredient}
        setVisibleModal={() => {
          setViewIngredient(false);
        }}
      />

      {visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={() => setVisibleDeleteModal(false)}
            approve={() => handleDeleteFarmGateProcess()}
            title={`Delete ${data?.processingName || ""} Process`}
            subTitle={`Are you sure you want to remove ${
              data?.processingName || ""
            } farm gate process? Please consider you cannot recover these data after remove`}
            style={{ zIndex: 10000 }}
            className="delete-confirmation-modal"
          />,
          document.body
        )}
    </>
  );
};
